import React, { useState } from "react";
import { NavLink, Outlet, Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Logo } from "@/components/layout/SiteHeader";
import {
  LayoutDashboard, Megaphone, Wallet, HeartHandshake, ShieldCheck, Banknote, Flag,
  ListChecks, LogOut, Menu, X, PlusCircle, ScrollText, UserCog, Settings, BarChart3, ExternalLink, Video, MessageSquarePlus, MessageSquare,
} from "lucide-react";
import { Button } from "@/components/ui/button";

const ORG_NAV = [
  { to: "/organizer/dashboard", label: "Dashboard", icon: LayoutDashboard },
  { to: "/organizer/campaigns", label: "My Campaigns", icon: Megaphone },
  { to: "/organizer/analytics", label: "Analytics", icon: BarChart3 },
  { to: "/organizer/onboarding", label: "Payout Onboarding", icon: Wallet },
  { to: "/organizer/payout-request", label: "Request Payout", icon: Banknote },
  { to: "/organizer/payouts", label: "Payout History", icon: ScrollText },
  { to: "/nonprofit/dashboard", label: "Nonprofit", icon: HeartHandshake },
];

const ADMIN_NAV = [
  { to: "/admin/reviews", label: "Review Queue", icon: ListChecks },
  { to: "/admin/videos", label: "Video Review", icon: Video },
  { to: "/admin/payouts", label: "Payout Queue", icon: Banknote },
  { to: "/admin/complaints", label: "Complaints & Risk", icon: Flag },
  { to: "/admin/feedback", label: "Beta Feedback", icon: MessageSquarePlus },
  { to: "/admin/sms-log", label: "SMS Log", icon: MessageSquare },
  { to: "/admin/audit", label: "Audit Log", icon: ScrollText },
];

const SUPERADMIN_NAV = [
  { to: "/admin/admins", label: "Admins", icon: UserCog },
  { to: "/admin/settings", label: "Platform Settings", icon: Settings },
];

export default function DashboardLayout({ admin = false }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const isSuper = user?.role === "superadmin";
  const nav = admin ? (isSuper ? [...ADMIN_NAV, ...SUPERADMIN_NAV] : ADMIN_NAV) : ORG_NAV;
  const homeHref = admin ? "/admin/reviews" : "/organizer/dashboard";

  return (
    <div className="flex min-h-screen bg-[#F3F4F6]">
      {/* Sidebar */}
      <aside className={`fixed inset-y-0 left-0 z-50 w-64 transform bg-ff-blue text-white transition-transform duration-200 lg:static lg:translate-x-0 ${open ? "translate-x-0" : "-translate-x-full"}`}>
        <div className="flex h-16 items-center justify-between border-b border-white/10 px-5">
          <Logo light to={homeHref} />
          <button className="lg:hidden" onClick={() => setOpen(false)} aria-label="Close menu">
            <X className="h-5 w-5" />
          </button>
        </div>
        <div className="px-4 py-3">
          <span className="rounded-full bg-white/10 px-3 py-1 text-xs font-bold uppercase tracking-wider text-white/80">
            {admin ? (isSuper ? "SuperAdmin" : "Admin Console") : "Organizer"}
          </span>
        </div>
        <nav className="flex flex-col gap-1 px-3">
          {nav.map((n) => {
            const Icon = n.icon;
            return (
              <NavLink key={n.to} to={n.to} onClick={() => setOpen(false)}
                end={n.to === homeHref}
                data-testid={`sidebar-${n.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                className={({ isActive }) =>
                  `flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-semibold transition-colors duration-200 ${isActive ? "bg-ff-red text-white" : "text-white/75 hover:bg-white/10 hover:text-white"}`}>
                <Icon className="h-4.5 w-4.5" /> {n.label}
              </NavLink>
            );
          })}
        </nav>
        {!admin && (
          <div className="px-4 py-5">
            <Button asChild className="w-full bg-white font-semibold text-ff-blue hover:bg-white/90">
              <Link to="/start-a-fundraiser" data-testid="sidebar-start-fundraiser">
                <PlusCircle className="mr-2 h-4 w-4" /> New Fundraiser
              </Link>
            </Button>
          </div>
        )}
        <div className="absolute bottom-0 w-full border-t border-white/10 p-4">
          <a href="/" target="_blank" rel="noopener noreferrer" data-testid="sidebar-view-site"
            className="mb-3 flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-semibold text-white/75 hover:bg-white/10 hover:text-white">
            <ExternalLink className="h-4 w-4" /> View public site
          </a>
          <div className="mb-2 truncate text-sm font-semibold">{user?.name}</div>
          <div className="mb-3 truncate text-xs text-white/60">{user?.email}</div>
          <button data-testid="sidebar-logout" onClick={() => { logout(); navigate("/"); }}
            className="flex w-full items-center gap-2 rounded-lg px-3 py-2 text-sm font-semibold text-white/75 hover:bg-white/10 hover:text-white">
            <LogOut className="h-4 w-4" /> Sign out
          </button>
        </div>
      </aside>

      {open && <div className="fixed inset-0 z-40 bg-black/40 lg:hidden" onClick={() => setOpen(false)} />}

      {/* Content */}
      <div className="flex min-w-0 flex-1 flex-col">
        <div className="flex h-16 items-center justify-between border-b border-ff-border bg-white px-5 lg:hidden">
          <button data-testid="dashboard-menu-toggle" onClick={() => setOpen(true)} aria-label="Open menu">
            <Menu className="h-6 w-6 text-ff-blue" />
          </button>
          <Logo to={homeHref} />
          <span className="w-6" />
        </div>
        <main className="ff-scroll flex-1 overflow-y-auto p-5 sm:p-8">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
